import React, { useState, useEffect } from 'react';
import './PaymentPage.css';
import AppHeader from './AppHeader';
import Toast from './Toast';
import { triggerHaptic } from '../services/telegram';
import apiService from '../services/api';

function PaymentPage({ user, onBack }) {
  const uid = user?.userid ?? user?.id;
  const [savedMethodTitle, setSavedMethodTitle] = useState(user?.saved_payment_method_title ?? null);
  const [hasSavedMethodFromApi, setHasSavedMethodFromApi] = useState(user?.has_saved_payment_method ?? false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    setSavedMethodTitle(user?.saved_payment_method_title ?? null);
    setHasSavedMethodFromApi(Boolean(user?.has_saved_payment_method));
  }, [user?.saved_payment_method_title, user?.has_saved_payment_method]);

  useEffect(() => {
    if (!uid) return;
    apiService.getUser(String(uid))
      .then((u) => {
        setSavedMethodTitle(u?.saved_payment_method_title ?? null);
        setHasSavedMethodFromApi(Boolean(u?.has_saved_payment_method));
      })
      .catch(() => {});
  }, [uid]);

  const handleDisableAutoRenewal = () => {
    triggerHaptic();
    if (!uid) {
      setToast({ isOpen: true, message: 'Ошибка: пользователь не найден', type: 'error' });
      return;
    }
    setIsConfirmVisible(true);
  };

  const handleConfirmDisable = async () => {
    triggerHaptic();
    setIsConfirmVisible(false);
    setIsDisabling(true);
    try {
      const res = await apiService.disableAutorenew(String(uid));
      setSavedMethodTitle(null);
      setHasSavedMethodFromApi(false);
      setToast({
        isOpen: true,
        message: res.cleared ? 'Автопродление отключено' : 'Автопродление уже было отключено',
        type: 'info',
      });
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.message || 'Не удалось отключить автопродление',
        type: 'error',
      });
    } finally {
      setIsDisabling(false);
    }
  };

  const handleCancelDisable = () => {
    triggerHaptic();
    setIsConfirmVisible(false);
  };

  const hasSavedMethod = Boolean(
    (savedMethodTitle && savedMethodTitle.trim()) || hasSavedMethodFromApi
  );
  const displaySavedMethodTitle = (savedMethodTitle && savedMethodTitle.trim()) || (hasSavedMethodFromApi ? 'Сохранённый способ' : '');

  return (
    <div className="payment-page">
      <div className="payment-background"></div>
      <AppHeader title="Оплата" />
      <div className="payment-content">
        <div className="payment-body">
          {hasSavedMethod ? (
            <>
              <div className="payment-methods-section">
                <span className="payment-methods-section-title">Сохранённый способ оплаты (автопродление)</span>
                <p className="payment-methods-section-desc">
                  С этого способа списывается оплата при автопродлении. С невыбранных способов платёж не списывается.
                </p>
                <div className="payment-methods-list">
                  <div className="payment-method-item payment-method-item-saved payment-method-item-selected">
                    <span className="payment-method-item-label">{displaySavedMethodTitle}</span>
                    <svg className="payment-method-item-check" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 5L7 14L4 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="payment-empty-state">
              <svg className="payment-empty-icon" width="64" height="64" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5C1 3.114 1 2.172 1.586 1.586C2.172 1 3.114 1 5 1H15C16.886 1 17.828 1 18.414 1.586C19 2.172 19 3.114 19 5V15C19 16.886 19 17.828 18.414 18.414C17.828 19 16.886 19 15 19H5C3.114 19 2.172 19 1.586 18.414C1 17.828 1 16.886 1 15V5Z" stroke="white" strokeWidth="2"/>
                <path d="M1 5H4C4.981 5 5.472 5 5.894 5.211C6.317 5.422 6.611 5.815 7.2 6.6L7.8 7.4C8.389 8.185 8.683 8.578 9.106 8.789C9.528 9 10.019 9 11 9H16C16.932 9 17.398 9 17.765 8.848C18.0078 8.74749 18.2284 8.6001 18.4143 8.41428C18.6001 8.22845 18.7475 8.00782 18.848 7.765C19 7.398 19 6.932 19 6" stroke="white" strokeWidth="2"/>
                <path d="M5 14H13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p className="payment-empty-text">У вас ещё нет добавленных способов оплаты</p>
            </div>
          )}

          {isConfirmVisible ? (
            <div className="payment-confirm-block">
              <p className="payment-confirm-text">Точно хотите отключить автопродление?</p>
              <div className="payment-confirm-buttons">
                <button
                  type="button"
                  className="payment-confirm-yes"
                  onClick={handleConfirmDisable}
                >
                  Да, отключить
                </button>
                <button
                  type="button"
                  className="payment-confirm-no"
                  onClick={handleCancelDisable}
                >
                  Нет
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="payment-auto-renewal-button"
              onClick={handleDisableAutoRenewal}
              disabled={isDisabling || !hasSavedMethod}
            >
              {isDisabling ? 'Отключаю…' : 'Отключить автопродление'}
            </button>
          )}
        </div>
      </div>
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
}

export default PaymentPage;
